import React from 'react';
import { motion } from 'framer-motion';
import { PauseIcon, PlayIcon } from 'lucide-react';
import type { Track } from '../types/music';
import { usePlayer } from '../contexts/PlayerContext';
interface TrackCardProps {
  track: Track;
  tracks: Track[];
  index: number;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function TrackCard({
  track,
  tracks,
  index
}: TrackCardProps) {
  const {
    play,
    currentTrack,
    isPlaying
  } = usePlayer();
  const active = currentTrack?.id === track.id;
  const playingThis = active && isPlaying;
  // Alternate the tilt direction so a row of cards fans out rather than
  // leaning the same way each time.
  const tilt = index % 2 === 0 ? -1.6 : 1.6;
  return <motion.button type="button" onClick={() => play(tracks, index)} aria-label={playingThis ? `Pause ${track.title}` : `Play ${track.title} by ${track.artist}`} initial={false} whileHover={{
    y: -10,
    scale: 1.04,
    rotate: tilt,
    zIndex: 10
  }} whileFocus={{
    y: -10,
    scale: 1.04,
    rotate: tilt,
    zIndex: 10
  }} whileTap={{
    scale: 0.99,
    rotate: 0
  }} transition={{
    duration: 0.22,
    ease: EASE
  }} className="group relative w-40 flex-none text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft sm:w-44" data-id="element-207" data-mp="1cbpwtralcujo">
      <span className="relative block overflow-hidden rounded-md" data-id="element-208" data-mp="rc4knj7ngm3e">
        {track.artwork ? <img src={track.artwork} alt={`${track.album} cover art`} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-300 ease-night group-hover:scale-[1.06]" data-id="element-209" data-mp="mdjgx9t6vka7" /> : <span className="block aspect-square w-full bg-ink-raised" data-id="element-210" data-mp="1a3409tt98jk0" />}
        {/* Sheen that crosses the cover on hover */}
        <span className="pointer-events-none absolute inset-0 -translate-x-full opacity-0 transition-[transform,opacity] duration-500 ease-night group-hover:translate-x-full group-hover:opacity-100" style={{
        background: 'linear-gradient(105deg, rgb(var(--cream) / 0) 35%, rgb(var(--cream) / 0.18) 50%, rgb(var(--cream) / 0) 65%)'
      }} aria-hidden="true" data-id="element-211" data-mp="3uv66tbpipu2y" />
        <span className={`absolute bottom-2 right-2 flex h-9 w-9 translate-y-2 items-center justify-center rounded-full bg-gold text-ink-base opacity-0 shadow-lg transition-[transform,opacity] duration-200 ease-night group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 ${active ? 'translate-y-0 opacity-100' : ''}`} data-id="element-212" data-mp="27z9b6baxx38g">
          {playingThis ? <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-213" data-mp="1p65gcmlzkkc9" /> : <PlayIcon className="h-4 w-4 translate-x-[1px]" aria-hidden="true" data-id="element-214" data-mp="1u7bb0pz7ihof" />}
        </span>
        <span className={`pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset transition-colors duration-200 ease-night ${active ? 'ring-gold/70' : 'ring-transparent group-hover:ring-gold/40'}`} aria-hidden="true" data-id="element-215" data-mp="1fakhuyg4m279" />
      </span>
      <span className={`mt-3 block truncate text-sm font-medium ${active ? 'text-gold' : 'text-cream'}`} data-id="element-216" data-mp="wnbezbapjp6a">
        {track.title}
      </span>
      <span className="mt-0.5 block truncate text-xs text-muted" data-id="element-217" data-mp="15mgae8qz32e8">{track.artist}</span>
    </motion.button>;
}